
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function CaseStudies() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Industries' },
    { id: 'mortgage', label: 'Mortgage Refinance' },
    { id: 'debt', label: 'Debt Consolidation' },
    { id: 'security', label: 'Home Security' },
    { id: 'ai', label: 'AI Solutions' }
  ];

  const caseStudies = [
    {
      id: 1,
      category: 'mortgage',
      title: 'Premier Mortgage Solutions',
      subtitle: '400% Increase in Qualified Mortgage Leads',
      description: 'How we transformed a regional mortgage company into a national leader through strategic SEO and Google Ads optimization.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20mortgage%20broker%20meeting%20with%20young%20couple%20in%20modern%20office%20discussing%20home%20loan%20documents%2C%20calculator%20and%20house%20model%20on%20desk%2C%20bright%20clean%20workspace%20atmosphere&width=600&height=400&seq=case1&orientation=landscape',
      results: [
        { metric: 'Lead Volume', value: '+400%' },
        { metric: 'Cost Per Lead', value: '-60%' },
        { metric: 'Conversion Rate', value: '+180%' },
        { metric: 'ROI', value: '+520%' }
      ],
      challenge: 'Premier Mortgage Solutions was struggling with high cost-per-acquisition and low-quality leads from traditional marketing channels.',
      solution: 'We implemented a comprehensive SEO strategy targeting long-tail keywords and created highly optimized Google Ads campaigns with advanced audience targeting.',
      timeline: '6 months',
      industry: 'Mortgage Refinance'
    },
    {
      id: 2,
      category: 'debt',
      title: 'Freedom Financial Group',
      subtitle: '350% Growth in Debt Consolidation Leads',
      description: 'Strategic multi-channel approach that positioned Freedom Financial as the leading debt consolidation provider in their market.',
      image: 'https://readdy.ai/api/search-image?query=Financial%20advisor%20consulting%20with%20stressed%20couple%20about%20debt%20consolidation%2C%20paperwork%20and%20calculator%20on%20conference%20table%2C%20professional%20counseling%20office%20environment%20with%20reassuring%20atmosphere&width=600&height=400&seq=case2&orientation=landscape',
      results: [
        { metric: 'Qualified Leads', value: '+350%' },
        { metric: 'Market Share', value: '+45%' },
        { metric: 'Brand Awareness', value: '+280%' },
        { metric: 'Customer LTV', value: '+190%' }
      ],
      challenge: 'Freedom Financial needed to compete against established players while maintaining compliance with strict financial advertising regulations.',
      solution: 'Developed compliant content marketing strategy combined with targeted Facebook and LinkedIn campaigns focused on financial education.',
      timeline: '8 months',
      industry: 'Debt Consolidation'
    },
    {
      id: 3,
      category: 'security',
      title: 'SecureHome Technologies',
      subtitle: '500% Increase in Home Security Installations',
      description: 'Complete digital transformation that turned SecureHome from a local installer into a regional security leader.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20security%20technician%20installing%20smart%20home%20security%20system%20with%20modern%20devices%20and%20control%20panel%2C%20homeowner%20watching%20demonstration%20in%20contemporary%20living%20room&width=600&height=400&seq=case3&orientation=landscape',
      results: [
        { metric: 'Installation Bookings', value: '+500%' },
        { metric: 'Service Area', value: '+300%' },
        { metric: 'Monthly Recurring Revenue', value: '+420%' },
        { metric: 'Customer Retention', value: '+95%' }
      ],
      challenge: 'SecureHome was limited to local referrals and had no digital presence in a competitive security market.',
      solution: 'Built comprehensive local SEO strategy, implemented Google My Business optimization, and created targeted PPC campaigns for high-intent keywords.',
      timeline: '4 months',
      industry: 'Home Security'
    },
    {
      id: 4,
      category: 'ai',
      title: 'IntelliLead AI Platform',
      subtitle: 'Revolutionizing Lead Qualification with AI',
      description: 'How our AI-powered lead scoring system increased qualification accuracy by 300% for a B2B software company.',
      image: 'https://readdy.ai/api/search-image?query=Data%20scientist%20analyzing%20AI%20algorithms%20on%20multiple%20computer%20screens%20showing%20machine%20learning%20models%20and%20predictive%20analytics%20charts%2C%20modern%20tech%20office%20with%20artificial%20intelligence%20focus&width=600&height=400&seq=case4&orientation=landscape',
      results: [
        { metric: 'Qualification Accuracy', value: '+300%' },
        { metric: 'Sales Team Efficiency', value: '+250%' },
        { metric: 'Conversion Rate', value: '+180%' },
        { metric: 'Sales Cycle Time', value: '-40%' }
      ],
      challenge: 'IntelliLead was wasting resources on unqualified leads and needed better prospect identification methods.',
      solution: 'Developed custom AI algorithms for lead scoring, implemented automated qualification workflows, and created predictive analytics dashboards.',
      timeline: '12 months',
      industry: 'AI Technology'
    },
    {
      id: 5,
      category: 'mortgage',
      title: 'Atlantic Refinance Corp',
      subtitle: 'Dominating Competitive Refinance Market',
      description: 'Strategic positioning and advanced targeting that helped Atlantic Refinance capture 30% market share in 18 months.',
      image: 'https://readdy.ai/api/search-image?query=Mortgage%20specialist%20presenting%20refinancing%20options%20to%20diverse%20family%20in%20modern%20bank%20office%2C%20financial%20charts%20and%20home%20images%20displayed%20on%20wall%20screens%2C%20professional%20consultation%20atmosphere&width=600&height=400&seq=case5&orientation=landscape',
      results: [
        { metric: 'Market Share', value: '+30%' },
        { metric: 'Loan Applications', value: '+600%' },
        { metric: 'Brand Recognition', value: '+400%' },
        { metric: 'Profit Margin', value: '+85%' }
      ],
      challenge: 'Atlantic Refinance entered a saturated market with limited brand recognition and tight competition.',
      solution: 'Created differentiated value proposition, implemented aggressive retargeting campaigns, and developed content marketing for financial education.',
      timeline: '18 months',
      industry: 'Mortgage Refinance'
    },
    {
      id: 6,
      category: 'debt',
      title: 'ClearPath Debt Solutions',
      subtitle: 'Building Trust in Debt Relief Industry',
      description: 'Transparent marketing approach that built consumer trust and generated 1000+ qualified leads monthly.',
      image: 'https://readdy.ai/api/search-image?query=Confident%20debt%20counselor%20shaking%20hands%20with%20relieved%20client%20after%20successful%20debt%20consolidation%20meeting%2C%20professional%20office%20with%20certificates%20and%20awards%20displayed%2C%20positive%20outcome%20atmosphere&width=600&height=400&seq=case6&orientation=landscape',
      results: [
        { metric: 'Monthly Leads', value: '1000+' },
        { metric: 'Trust Score', value: '+400%' },
        { metric: 'Client Satisfaction', value: '+95%' },
        { metric: 'Referral Rate', value: '+300%' }
      ],
      challenge: 'ClearPath needed to overcome industry skepticism and build consumer trust in a heavily regulated market.',
      solution: 'Developed educational content strategy, implemented transparent pricing displays, and created customer success story campaigns.',
      timeline: '10 months',
      industry: 'Debt Consolidation'
    }
  ];

  const filteredCaseStudies = selectedFilter === 'all' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === selectedFilter);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.8), rgba(30, 64, 175, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20business%20analytics%20dashboard%20showing%20successful%20marketing%20campaign%20results%20with%20growth%20charts%20and%20performance%20metrics%20on%20large%20screens%2C%20modern%20corporate%20meeting%20room&width=1920&height=800&seq=case-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Proven Results That Speak for Themselves
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              Discover how we've helped international clients achieve remarkable growth through strategic lead generation and digital marketing excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                <i className="ri-chat-3-line mr-2"></i>
                Discuss Your Goals
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/services')}
                className="border-white text-white hover:bg-white hover:text-blue-600"
              >
                <i className="ri-service-line mr-2"></i>
                View Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-gray-50 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedFilter === filter.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {filteredCaseStudies.map((study) => (
              <div key={study.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={study.image} 
                  alt={study.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                      {study.industry}
                    </span>
                    <span className="text-gray-500 text-sm">{study.timeline}</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{study.title}</h3>
                  <h4 className="text-lg font-semibold text-blue-600 mb-4">{study.subtitle}</h4>
                  <p className="text-gray-600 mb-6 leading-relaxed">{study.description}</p>
                  
                  {/* Results Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {study.results.map((result, index) => (
                      <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 mb-1">{result.value}</div>
                        <div className="text-sm text-gray-600">{result.metric}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Challenge & Solution */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Challenge:</h5>
                      <p className="text-sm text-gray-600">{study.challenge}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Solution:</h5>
                      <p className="text-sm text-gray-600">{study.solution}</p>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/contact')}
                    className="w-full"
                  >
                    <i className="ri-chat-3-line mr-2"></i>
                    Get Similar Results
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Aggregate Success Metrics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Combined results across all our case studies demonstrate consistent, measurable success for our international clients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-5xl font-bold text-blue-600 mb-4">$500M+</div>
              <div className="text-gray-600 font-medium">Total Client Revenue Generated</div>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-5xl font-bold text-blue-600 mb-4">2M+</div>
              <div className="text-gray-600 font-medium">Qualified Leads Delivered</div>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-5xl font-bold text-blue-600 mb-4">350%</div>
              <div className="text-gray-600 font-medium">Average ROI Increase</div>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-5xl font-bold text-blue-600 mb-4">95%</div>
              <div className="text-gray-600 font-medium">Client Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Become Our Next Success Story?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss how we can replicate these results for your business and drive similar growth in your industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              <i className="ri-phone-line mr-2"></i>
              Schedule Strategy Call
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-blue-600"
            >
              <i className="ri-eye-line mr-2"></i>
              Explore Our Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
